/**
 * Single source of truth for ICS-204 status values, their display labels, and
 * which transitions a user may pick in edit mode.
 *
 * The card has two status columns. `proposedStatus` is what the user asks for;
 * `status` (referred to as the actual status) is what the backend derives from
 * it. Asking for `completed` or `incomplete` does not set those values: the
 * backend stores `awaiting_review` and opens an approval request, so the
 * display label has to combine both columns.
 */

export type Status =
  | 'awaiting_review'
  | 'draft'
  | 'in_progress'
  | 'completed'
  | 'incomplete';

export type ProposedStatusOption =
  | null
  | 'Draft'
  | 'Assigned'
  | 'Submitted Complete for Review'
  | 'Reported Incomplete for Review'
  | 'Completed'
  | 'Incomplete';

/** Label for a status the user is choosing, before the backend derives anything. */
const PROPOSED_STATUS_LABELS: Record<Status, string> = {
  awaiting_review: 'Awaiting Review',
  draft: 'Draft',
  in_progress: 'Assigned',
  completed: 'Submit Complete for Review',
  incomplete: 'Report Incomplete for Review',
};

export const mapProposedStatus = (
  proposedStatus: Status | null,
  actualStatus: Status | null
): ProposedStatusOption => {
  switch (proposedStatus) {
    case 'draft':
      return 'Draft';
    case 'in_progress':
      return 'Assigned';
    case 'completed':
      if (actualStatus === 'awaiting_review') {
        return 'Submitted Complete for Review';
      }
      return 'Completed';
    case 'incomplete':
      if (actualStatus === 'awaiting_review') {
        return 'Reported Incomplete for Review';
      }
      return 'Incomplete';
    default:
      return null;
  }
};

/** Inverse of {@link mapProposedStatus}, for seeding the edit form. */
export const toProposedStatusEnum = (status: ProposedStatusOption): Status => {
  switch (status) {
    case 'Assigned':
      return 'in_progress';
    case 'Completed':
    case 'Submitted Complete for Review':
      return 'completed';
    case 'Incomplete':
    case 'Reported Incomplete for Review':
      return 'incomplete';
    case 'Draft':
    default:
      return 'draft';
  }
};

/**
 * Statuses a user may select given where the card currently is, mirroring the
 * transitions the view-mode action buttons already allow. A card in
 * `awaiting_review` cannot be edited at all, so it only ever sees itself.
 */
export const allowedProposedStatuses = (
  actualStatus: Status | null,
  currentProposedStatus: Status
): Status[] => {
  const withCurrent = (statuses: Status[]): Status[] =>
    statuses.includes(currentProposedStatus)
      ? statuses
      : [currentProposedStatus, ...statuses];

  switch (actualStatus) {
    case 'awaiting_review':
      return [currentProposedStatus];
    case 'in_progress':
      return withCurrent(['in_progress', 'completed', 'incomplete']);
    case 'completed':
    case 'incomplete':
      return withCurrent(['draft', 'in_progress']);
    case 'draft':
    default:
      return withCurrent(['draft', 'in_progress']);
  }
};

export const statusOptions = (
  actualStatus: Status | null,
  currentProposedStatus: Status,
  iapApproved = true
): { value: Status; label: string }[] =>
  allowedProposedStatuses(actualStatus, currentProposedStatus)
    .filter(
      (value) =>
        iapApproved ||
        value !== 'in_progress' ||
        value === currentProposedStatus
    )
    .map((value) => ({
      value,
      label: PROPOSED_STATUS_LABELS[value],
    }));

/**
 * Warning shown before saving a status that does more than change a column:
 * requesting review locks the card, and assigning notifies the work group.
 */
export const statusSideEffectWarning = (status: Status): string | null => {
  switch (status) {
    case 'completed':
      return 'This submits the assignment for review. A reviewer will be assigned and the card will be locked from further editing until they respond.';
    case 'incomplete':
      return 'This reports the assignment as incomplete and submits it for review. A reviewer will be assigned and the card will be locked from further editing until they respond.';
    case 'in_progress':
      return 'This assigns the work to the work group and notifies its leaders.';
    default:
      return null;
  }
};

/** Hover tooltip on the Assignee label / column header info icon. */
export const ICS204_ASSIGNEE_COLUMN_TOOLTIP =
  'The assignee is the Group/Division/Branch/etc that is assigned this 204. After this 204 is approved, you can assign it to the assignee, which will be notified and can mark it complete or incomplete.';

/**
 * Shared Draft → Assigned handoff copy (Assign button + Status column).
 * Mentions that work-group leaders are notified.
 */
export const ICS204_ASSIGN_HANDOFF_TOOLTIP =
  'Hands this ICS-204 to the work group and moves it from Draft to Assigned. This notifies the work group’s leaders. The work group can then submit it as complete or report that it cannot be completed.';

/** Shown when Assign is closed because this period's IAP cover is not signed yet. */
export const ICS204_ASSIGN_NEEDS_IAP =
  'The Incident Action Plan must be approved before this ICS-204 can be assigned.';

/**
 * Column-header tooltip: meaning of Status values, including Draft → Assigned
 * handoff.
 */
export const ICS204_STATUS_COLUMN_TOOLTIP = `${ICS204_ASSIGN_HANDOFF_TOOLTIP} Other statuses: Submitted Complete / Reported Incomplete for Review means the work group asked a reviewer to accept or deny the outcome; Completed and Incomplete are the reviewer’s final results.`;

export const ICS204_ADD_DISABLED_HISTORICAL =
  'Viewing a historical version (read-only).';

export const ICS204_ADD_DISABLED_SIGNED_LOCKED =
  'Signed versions are locked from edits.';

export const ICS204_ADD_DISABLED_NO_PERMISSION =
  'You do not have permission to edit this assignment.';

export const ICS204_ADD_DISABLED_AWAITING_REVIEW =
  'This assignment is awaiting review and is locked from edits.';

export const ICS204_ADD_DISABLED_NO_WALA =
  'Save this assignment card before adding items.';

export const ICS204_ADD_DISABLED_NO_WORK_GROUP =
  'Assign a work group before adding items.';

export type Ics204AddLockContext = {
  viewingVersionId?: string | null;
  contentLocked?: boolean;
  readOnly?: boolean;
  isCreatingSignedVersion?: boolean;
  walaId?: string | null;
  workGroup?: string | null;
  actualStatus?: string | null;
};

/**
 * Tooltip copy when an ICS-204 “Add …” control is visible but disabled. Returns
 * null when adding is allowed.
 */
export function getIcs204AddDisabledReason(
  ctx: Ics204AddLockContext
): string | null {
  if (ctx.viewingVersionId) return ICS204_ADD_DISABLED_HISTORICAL;
  if (ctx.contentLocked) return ICS204_ADD_DISABLED_SIGNED_LOCKED;
  if (ctx.readOnly) return ICS204_ADD_DISABLED_NO_PERMISSION;
  if (ctx.isCreatingSignedVersion) {
    return 'Finish or cancel creating a signed version before adding items.';
  }
  if (ctx.actualStatus === 'awaiting_review') {
    return ICS204_ADD_DISABLED_AWAITING_REVIEW;
  }
  if (ctx.walaId == null) return ICS204_ADD_DISABLED_NO_WALA;
  if (!ctx.workGroup) return ICS204_ADD_DISABLED_NO_WORK_GROUP;
  return null;
}
